import { ArrowRight, Clock, MapPin, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { EmptyState, Skeleton } from '../../../components';
import { useLang } from '../../../i18n';
import type { OfficeLocation } from '../../../types';
import { buildMapEmbedUrl } from '../../../utils/mapEmbed';
import './OfficesTeaserSection.scss';

export interface OfficesTeaserSectionProps {
  offices: OfficeLocation[];
  loading: boolean;
}

function pickPrimary(list: OfficeLocation[]): OfficeLocation | null {
  if (list.length === 0) return null;
  return list.find((o) => o.is_primary) || list[0];
}

export default function OfficesTeaserSection({ offices, loading }: OfficesTeaserSectionProps) {
  const { t } = useLang();
  const primary = pickPrimary(offices);
  const showSkeleton = loading && offices.length === 0;
  const empty = !loading && offices.length === 0;

  return (
    <section className="section section--tight about-office">
      <div className="container">
        <div className="section-header about-office__head">
          <span className="eyebrow">{t('about.office.t')}</span>
          <h2 className="about-office__title">{t('about.office.sub')}</h2>
        </div>

        {empty || !primary ? (
          showSkeleton ? (
            <div className="about-office__grid">
              <Skeleton width="100%" height={260} />
              <Skeleton width="100%" height={260} />
            </div>
          ) : (
            <EmptyState title={t('about.empty')} />
          )
        ) : (
          <>
            <div className="card-frame about-office__grid">
              <div className="about-office__info">
                <h3 className="about-office__name">{primary.name || primary.city}</h3>
                {primary.address ? (
                  <p className="about-office__row">
                    <MapPin size={16} aria-hidden="true" />
                    <span>{primary.address}</span>
                  </p>
                ) : null}
                {primary.phone ? (
                  <p className="about-office__row">
                    <Phone size={16} aria-hidden="true" />
                    <a href={`tel:${primary.phone}`}>{primary.phone}</a>
                  </p>
                ) : null}
                {primary.hours ? (
                  <p className="about-office__row">
                    <Clock size={16} aria-hidden="true" />
                    <span>{primary.hours}</span>
                  </p>
                ) : null}
              </div>
              <div className="about-office__map">
                {(() => {
                  const src = buildMapEmbedUrl(primary);
                  return src ? (
                    <iframe
                      title={primary.name || primary.city}
                      src={src}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      className="about-office__map-iframe"
                    />
                  ) : (
                    <div className="about-office__map-fallback">
                      <MapPin size={28} aria-hidden="true" />
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className="about-office__cta">
              <Link to="/contacts" className="about-office__cta-link">
                <span>{t('about.office.cta')}</span>
                <ArrowRight size={16} />
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
